'use client';

import { FunctionalIcon } from '@/app/Components/FunctionalIcon';
import React, { useState, useEffect } from 'react';
import { RutgersCourse, UserAPCredit } from '@/app/types/interfaces';
import { 
  checkPrerequisites, 
  getCoursesWithPrerequisites,
  PrerequisiteCheck,
  formatPrerequisiteText 
} from '@/app/services/prerequisiteChecker';
import { loadCatalogCourses as getParsedCourses } from '@/app/services/courseCatalogClient';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { formatBaseIdDisplay } from '@/app/lib/utils';
import { CourseDependencyNeighborhood } from './CourseDependencyNeighborhood';

interface PrerequisiteCheckerProps {
  completedCourses: RutgersCourse[];
  apCredits?: UserAPCredit[];
  onCourseSelect?: (course: RutgersCourse) => void;
  initialQuery?: string;
}

export default function PrerequisiteChecker({ 
  completedCourses, 
  apCredits = [],
  onCourseSelect,
  initialQuery = '',
}: PrerequisiteCheckerProps) {
  const [allCourses, setAllCourses] = useState<RutgersCourse[]>([]);
  const [prerequisiteChecks, setPrerequisiteChecks] = useState<PrerequisiteCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState<RutgersCourse | null>(null);
  const [searchQuery, setSearchQuery] = useState(initialQuery);

  useEffect(() => setSearchQuery(initialQuery), [initialQuery]);

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const courses = await getParsedCourses();
        setAllCourses(courses);
        
        const coursesWithPrereqs = getCoursesWithPrerequisites(courses);
        
        // Perform prerequisite checks with AP credits
        const checks = coursesWithPrereqs.map(course =>
          checkPrerequisites(course, completedCourses, apCredits)
        );
        setPrerequisiteChecks(checks);
      } catch (error) {
        console.error('Error loading courses:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, [completedCourses, apCredits]);

  const filteredChecks = prerequisiteChecks.filter(check => {
    if (!searchQuery) return true;
    
    const courseName = check.course.courseName.toLowerCase();
    const department = check.course.department.toLowerCase();
    const classNumber = check.course.class_number.toLowerCase();
    
    const query = searchQuery.toLowerCase();
    
    // Handle colon-separated search (e.g., "640:250")
    if (query.includes(':')) {
      const parts = query.split(':');
      if (parts.length === 2) {
        const searchDept = parts[0].trim();
        const searchNum = parts[1].trim();
        return department.includes(searchDept) && classNumber.includes(searchNum);
      }
    }
    
    // Regular search (existing logic)
    return courseName.includes(query) || 
           department.includes(query) || 
           classNumber.includes(query);
  });

  const satisfiedChecks = filteredChecks
    .filter(check => check.isSatisfied)
    .filter((check, idx, arr) => arr.findIndex(c => c.course.baseId === check.course.baseId) === idx);
  const unsatisfiedChecks = filteredChecks
    .filter(check => !check.isSatisfied)
    .filter((check, idx, arr) => arr.findIndex(c => c.course.baseId === check.course.baseId) === idx);

  const handleCourseSelect = (course: RutgersCourse) => {
    setSelectedCourse(course);
    onCourseSelect?.(course);
  };

  const getStatusIcon = (check: PrerequisiteCheck) => {
    if (check.error) {
      return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
    }
    return check.isSatisfied ? 
      <CheckCircleIcon className="h-5 w-5 text-green-500" /> :
      <XCircleIcon className="h-5 w-5 text-red-500" />;
  };

  const getStatusText = (check: PrerequisiteCheck) => {
    if (check.error) return 'Error';
    return check.isSatisfied ? 'Prerequisites Satisfied' : 'Prerequisites Not Met';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
        <span className="ml-2 text-slate-600 dark:text-slate-400">Loading courses...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-4">
        <label htmlFor="prerequisite-course-search" className="mb-2 block text-sm font-medium text-[var(--ss-text)]">Find a course</label>
        <div className="relative">
          <input
            id="prerequisite-course-search"
            type="text"
            aria-label="Search courses to check prerequisites"
            placeholder="Course name or code"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="min-h-12 w-full rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface)] px-3 text-[var(--ss-text)] placeholder:text-[var(--ss-text-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"
          />
        </div>
        <p className="mt-2 text-xs text-[var(--ss-text-muted)]">Advisory only. Rutgers determines registration eligibility.</p>
      </div>

      {/* Course Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Available Courses */}
        <div className="bg-white dark:bg-slate-800 rounded-control shadow-e1">
          <div className="p-6 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              Prerequisites met ({satisfiedChecks.length})
            </h2>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {satisfiedChecks.length === 0 ? (
              <div className="p-6 text-center text-slate-500 dark:text-slate-400">
                No matching courses have all listed prerequisites met.
              </div>
            ) : (
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {satisfiedChecks.map((check) => (
                  <button
                    key={check.course.id}
                    type="button"
                    aria-label={`View prerequisites for ${check.course.courseName}`}
                    aria-pressed={selectedCourse?.baseId === check.course.baseId}
                    className="min-h-12 w-full p-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ss-focus)]"
                    onClick={() => handleCourseSelect(check.course)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-slate-900 dark:text-white truncate" title={check.course.courseName}>
                          {check.course.courseName}
                        </span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                            {check.course.credits} credits
                          </span>
                          {check.satisfiedPrerequisites.length > 0 && (
                            <span className="text-xs text-green-700 dark:text-green-400 font-medium">
                              <FunctionalIcon name="complete" /> {check.satisfiedPrerequisites.length} met
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0">{getStatusIcon(check)}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Courses with Missing Prerequisites */}
        <div className="bg-white dark:bg-slate-800 rounded-control shadow-e1">
          <div className="p-6 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
              <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
              Missing prerequisites ({unsatisfiedChecks.length})
            </h2>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {unsatisfiedChecks.length === 0 ? (
              <div className="p-6 text-center text-slate-500 dark:text-slate-400">
                No matching courses have missing prerequisites.
              </div>
            ) : (
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {unsatisfiedChecks.map((check) => (
                  <button
                    key={check.course.id}
                    type="button"
                    aria-label={`View prerequisites for ${check.course.courseName}`}
                    aria-pressed={selectedCourse?.baseId === check.course.baseId}
                    className="min-h-12 w-full p-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ss-focus)]"
                    onClick={() => handleCourseSelect(check.course)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-slate-900 dark:text-white truncate" title={check.course.courseName}>
                          {check.course.courseName}
                        </span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                            {check.course.credits} credits
                          </span>
                          {check.missingPrerequisites.length > 0 && (
                            <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                              <FunctionalIcon name="missing" /> {check.missingPrerequisites.length} missing
                            </span>
                          )}
                        </div>
                        {check.prerequisites && (
                          <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 line-clamp-1 italic">
                            Req: {formatPrerequisiteText(check.prerequisites)}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0">{getStatusIcon(check)}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Selected Course Details */}
      {selectedCourse && (
        <>
        <div className="bg-white dark:bg-slate-800 rounded-control p-6 shadow-e1">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            {selectedCourse.courseName} <span className="ml-2 font-mono text-sm font-normal text-content-muted">{formatBaseIdDisplay(selectedCourse.baseId)}</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-slate-900 dark:text-white mb-2">Course Information</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Credits:</span> {selectedCourse.credits}</p>
                <p><span className="font-medium">Campus:</span> {selectedCourse.campus}</p>
                {selectedCourse.prerequisites && (
                  <p><span className="font-medium">Prerequisites:</span> {formatPrerequisiteText(selectedCourse.prerequisites)}</p>
                )}
              </div>
            </div>
            
            <div>
              <h3 className="font-medium text-slate-900 dark:text-white mb-2">Prerequisite Status</h3>
              {(() => {
                const check = checkPrerequisites(selectedCourse, completedCourses, apCredits);
                return (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      {getStatusIcon(check)}
                      <span className="ml-2">{getStatusText(check)}</span>
                    </div>
                    {check.satisfiedPrerequisites.length > 0 && (
                      <p className="text-green-700 dark:text-green-400">
                        <FunctionalIcon name="complete" /> Satisfied: {check.satisfiedPrerequisites.join(', ')}
                      </p>
                    )}
                    {check.missingPrerequisites.length > 0 && (
                      <p className="text-red-600 dark:text-red-400">
                        <FunctionalIcon name="missing" /> Missing: {check.missingPrerequisites.join(', ')}
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
        <CourseDependencyNeighborhood course={selectedCourse} catalog={allCourses} onSelect={setSelectedCourse} />
        </>
      )}
    </div>
  );
}
